import React, { useEffect, useState } from "react";
import styles from "./style.module.scss";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../../state";
import {
  MutateTPMSConfig,
  MutateUsingAKSWeather,
} from "../../../../utils/graphql/hooks/TPMSHooks";
import { setTPMSConfig } from "../../../../state/tpmsReducer";

interface ConfigDialogProps {
  isOpen: boolean;
  showDialog: (newState: boolean) => void;
}

function shake() {
  const dialog = document.getElementsByClassName("dialog");
  if (dialog.length > 0) {
    for (const el of dialog) {
      el.classList.add(styles.shaking);
      setTimeout(() => {
        el.classList.remove(styles.shaking, styles.shouldGrow);
      }, 500);
    }
  }
}

export default function ConfigDialog(props: ConfigDialogProps) {
  const { isOpen, showDialog } = props;
  const tpmsConfig = useSelector((state: RootState) => state.tpms.tpmsConfig);
  const usingAKSWeather = useSelector(
    (state: RootState) => state.tpms.usingAKSWeather,
  );
  const [localUsingAKSWeather, setLocalUsingAKSWeather] =
    useState(usingAKSWeather);
  const updateTPMSConfig = MutateTPMSConfig();
  const updateUsingAKSWeather = MutateUsingAKSWeather();

  useEffect(() => {
    setLocalUsingAKSWeather(usingAKSWeather);
  }, [usingAKSWeather]);

  useEffect(() => {
    const tAmbientInputElement = document.getElementById(
      `tAmbientInput`,
    ) as HTMLInputElement;

    const pWarningInputElement = document.getElementById(
      `pWarningLimit`,
    ) as HTMLInputElement;

    const pAtmInputElement = document.getElementById(
      `pAtmInput`,
    ) as HTMLInputElement;

    const tGarageInputElement = document.getElementById(
      `tGarageInput`,
    ) as HTMLInputElement;

    const kHeatTransferAirInputElement = document.getElementById(
      `kHeatTransferAirInput`,
    ) as HTMLInputElement;

    const kHeatTransferTyreInputElement = document.getElementById(
      `kHeatTransferTyreInput`,
    ) as HTMLInputElement;

    if (tAmbientInputElement) {
      tAmbientInputElement.value = String(tpmsConfig?.tAmbient || "0");
    }

    if (pWarningInputElement) {
      pWarningInputElement.value = String(tpmsConfig?.pWarning || "0");
    }

    if (pAtmInputElement) {
      pAtmInputElement.value = String(tpmsConfig?.pAtm || "0");
    }

    if (tGarageInputElement) {
      tGarageInputElement.value = String(tpmsConfig?.tGarage || "0");
    }

    if (kHeatTransferAirInputElement) {
      kHeatTransferAirInputElement.value = String(
        tpmsConfig?.kHeatTransferAir || "0",
      );
    }

    if (kHeatTransferTyreInputElement) {
      kHeatTransferTyreInputElement.value = String(
        tpmsConfig?.kHeatTransferTyre || "0",
      );
    }
  }, [tpmsConfig]);

  function closeDialog() {
    showDialog(false);
  }

  const dispatch = useDispatch();
  function submit(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault();

    updateUsingAKSWeather({
      variables: { usingAKSWeather: localUsingAKSWeather },
    });

    const tAmbientInputElement = document.getElementById(
      `tAmbientInput`,
    ) as HTMLInputElement;

    const pWarningInputElement = document.getElementById(
      `pWarningLimit`,
    ) as HTMLInputElement;

    const pAtmInputElement = document.getElementById(
      `pAtmInput`,
    ) as HTMLInputElement;

    const tGarageInputElement = document.getElementById(
      `tGarageInput`,
    ) as HTMLInputElement;

    const kHeatTransferAirInputElement = document.getElementById(
      `kHeatTransferAirInput`,
    ) as HTMLInputElement;

    const kHeatTransferTyreInputElement = document.getElementById(
      `kHeatTransferTyreInput`,
    ) as HTMLInputElement;
    if (!pWarningInputElement.value || !pAtmInputElement.value) {
      shake();
    } else {
      const newTPMSConfig = {
        tAmbient: parseFloat(tAmbientInputElement.value),
        pAtm: parseFloat(pAtmInputElement.value),
        pWarning: parseFloat(pWarningInputElement.value),
        tGarage: parseFloat(tGarageInputElement.value),
        kHeatTransferAir: parseFloat(kHeatTransferAirInputElement.value),
        kHeatTransferTyre: parseFloat(kHeatTransferTyreInputElement.value),
      };
      dispatch(setTPMSConfig(newTPMSConfig)); //TODO: revert this if the mutation returns error
      updateTPMSConfig({
        variables: newTPMSConfig,
      });

      closeDialog();
    }
  }

  return (
    <div
      style={!isOpen ? { display: "none" } : {}}
      className={styles.dialogContainer}
    >
      <div className={`${styles.dialog} ${styles.shouldGrow} dialog`}>
        <div className={styles.title}>Config</div>
        <form>
          <div>
            <p>
              <label>Use AKS Weather:</label>
              <input
                id="usingAKSWeatherInput"
                type="checkbox"
                checked={localUsingAKSWeather}
                onChange={(e) => {
                  setLocalUsingAKSWeather(e.target.checked);
                }}
              />
            </p>
            <p>
              <label>Ambient Temperature:</label>
              <input
                className={`${styles.dialogInput}`}
                step="0.1"
                id="tAmbientInput"
                type="number"
                autoFocus
                disabled={localUsingAKSWeather === true}
              />
              <label>°C</label>
            </p>
            <p>
              <label>Atmospheric Pressure:</label>
              <input
                className={`${styles.dialogInput}`}
                step="0.001"
                id="pAtmInput"
                type="number"
                autoFocus
                disabled={localUsingAKSWeather === true}
              />
              <label>bar</label>
            </p>
            <p>
              <label>Garage Temperature:</label>
              <input
                className={`${styles.dialogInput}`}
                step="0.1"
                id="tGarageInput"
                type="number"
                autoFocus
              />
              <label>°C</label>
            </p>
            <p>
              <label>Heat Transfer Coefficient (Air):</label>
              <input
                className={`${styles.dialogInput}`}
                step="0.000001"
                id="kHeatTransferAirInput"
                type="number"
                autoFocus
              />
            </p>
            <p>
              <label>Heat Transfer Coefficient (Tyre):</label>
              <input
                className={`${styles.dialogInput}`}
                step="0.000001"
                id="kHeatTransferTyreInput"
                type="number"
                autoFocus
              />
            </p>
            <p>
              <label>Pressure Warning YLine:</label>
              <input
                className={`${styles.dialogInput}`}
                step="0.01"
                id="pWarningLimit"
                type="number"
                autoFocus
              />
              <label>bar</label>
            </p>
          </div>
          <div>
            <button className={styles.submitButton} onClick={submit}>
              Submit
            </button>
          </div>
        </form>
      </div>
      <div
        className={styles.backgroundBlur}
        onClick={() => closeDialog()}
      ></div>
    </div>
  );
}
